import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, Spin, Tooltip } from 'antd';
import { TeamOutlined, FileTextOutlined, MessageOutlined, UserSwitchOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { getDashboard, getDashboardTrend, DashboardStats, DashboardTrend } from '../../api/admin';

const TrendChart: React.FC<{ data: { date: string; count: number }[]; title: string; color: string; tooltip?: string }> = ({ data, title, color, tooltip }) => {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <Card
      title={
        <span>
          {title}
          {tooltip && (
            <Tooltip title={tooltip}>
              <QuestionCircleOutlined style={{ color: '#999', marginLeft: 6, fontSize: 13, cursor: 'pointer' }} />
            </Tooltip>
          )}
        </span>
      }
      size="small"
      style={{ height: '100%' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', height: 120, gap: 4 }}>
        {data.map((item) => (
          <div key={item.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: 10, color: '#666', marginBottom: 4 }}>{item.count}</div>
            <div
              style={{
                width: '100%',
                height: `${(item.count / max) * 80}px`,
                background: color,
                borderRadius: '4px 4px 0 0',
                minHeight: 2,
              }}
            />
            <div style={{ fontSize: 10, color: '#999', marginTop: 4, transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
              {item.date.slice(5)}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [trend, setTrend] = useState<DashboardTrend | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getDashboard(), getDashboardTrend()])
      .then(([s, t]) => {
        setStats(s);
        setTrend(t);
      })
      .finally(() => setLoading(false));
  }, []);

  const cards = [
    { title: '总用户数', value: stats?.totalUsers, icon: <TeamOutlined />, color: '#1890ff', tooltip: '统计系统中所有已注册用户的总数（users 表全部记录）' },
    { title: '今日记录', value: stats?.todayLogs, icon: <FileTextOutlined />, color: '#52c41a', tooltip: '统计当天（00:00-23:59）用户提交的饮食记录总数（food_logs 表中 log_date 为今天的记录）' },
    { title: '会话总数', value: stats?.totalSessions, icon: <MessageOutlined />, color: '#722ed1', tooltip: '统计系统中所有语音会话的总数（voice_sessions 表全部记录）' },
    { title: '活跃用户', value: stats?.activeUsers, icon: <UserSwitchOutlined />, color: '#fa8c16', tooltip: '统计过去 7 天内有登录行为的用户数（users 表中 last_login_at 在最近 7 天内的用户，去重）' },
  ];

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {cards.map((card) => (
          <Col xs={24} sm={12} lg={6} key={card.title}>
            <Card>
              <Statistic
                title={
                  <span>
                    {card.title}
                    <Tooltip title={card.tooltip}>
                      <QuestionCircleOutlined style={{ color: '#999', marginLeft: 6, fontSize: 13, cursor: 'pointer' }} />
                    </Tooltip>
                  </span>
                }
                value={card.value ?? '-'}
                prefix={React.cloneElement(card.icon, { style: { color: card.color } })}
              />
            </Card>
          </Col>
        ))}
      </Row>
      {trend && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={8}>
            <TrendChart data={trend.dailyUsers} title="近7天新注册用户" color="#1890ff" tooltip="统计过去 7 天内每天新注册的用户数（按 created_at 分组）" />
          </Col>
          <Col xs={24} lg={8}>
            <TrendChart data={trend.dailyLogs} title="近7天饮食记录" color="#52c41a" tooltip="统计过去 7 天内每天用户提交的饮食记录数（按 log_date 分组）" />
          </Col>
          <Col xs={24} lg={8}>
            <TrendChart data={trend.dailySessions} title="近7天语音会话" color="#722ed1" tooltip="统计过去 7 天内每天发起的语音会话数（按 created_at 分组）" />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Dashboard;
